import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { getAuth, createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { db } from "../../lib/firebase";
import { doc, setDoc } from "firebase/firestore";

export const JoinPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    terms: false,
  });

  // Trusted email providers
  const trustedProviders = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
    'icloud.com', 'me.com', 'mac.com', 'protonmail.com', 'tutanota.com',
    'zoho.com', 'yandex.com', 'mail.ru', 'qq.com', '163.com',
    '126.com', 'sina.com', 'sohu.com', 'naver.com', 'daum.net',
    'web.de', 'gmx.de', 't-online.de', 'freenet.de', 'arcor.de',
    'orange.fr', 'laposte.net', 'free.fr', 'wanadoo.fr', 'sfr.fr',
    'libero.it', 'virgilio.it', 'alice.it', 'tiscali.it', 'tim.it',
    'rediffmail.com', 'sify.com', 'indiatimes.com', 'rediff.com',
    'company.com', 'business.com', 'corporate.com', 'enterprise.com'
  ];

  // Blocked temporary email providers
  const blockedProviders = [
    '10minutemail.com', 'guerrillamail.com', 'mailinator.com', 'tempmail.org',
    'temp-mail.org', 'sharklasers.com', 'grr.la', 'guerrillamailblock.com',
    'pokemail.net', 'spam4.me', 'bccto.me', 'chacuo.net', 'dispostable.com',
    'fakeinbox.com', 'fakemailgenerator.com', 'getairmail.com', 'mailnesia.com',
    'mintemail.com', 'mytrashmail.com', 'nwldx.com', 'sharklasers.com',
    'spamgourmet.com', 'tempr.email', 'throwaway.email', 'yopmail.com',
    'yopmail.net', 'yopmail.org', 'cool.fr.nf', 'jetable.fr.nf',
    'nospam.ze.tc', 'nomail.xl.cx', 'mega.zik.dj', 'speed.1s.fr',
    'courriel.fr.nf', 'moncourrier.fr.nf', 'monemail.fr.nf', 'monmail.fr.nf',
    'mailcatch.com', 'maildrop.cc', 'mailnesia.com', 'mailnull.com',
    'mailinator.net', 'mailinator.org', 'mailinator.com', 'mailinator2.com',
    'mailinator3.com', 'mailinator4.com', 'mailinator5.com', 'mailinator6.com',
    'mailinator7.com', 'mailinator8.com', 'mailinator9.com', 'mailinator10.com',
    'temp-mail.ru', 'tmpmail.net', 'tmpmail.org', 'tmpeml.com',
    'tmpbox.net', 'tmpmail.com', 'tmpeml.org', 'tmpmail.ru',
    '10minutemail.net', '10minutemail.org', '10minutemail.com',
    'minuteinbox.com', 'minuteinbox.net', 'minuteinbox.org',
    'throwawaymail.com', 'throwawaymail.net', 'throwawaymail.org',
    'disposablemail.com', 'disposablemail.net', 'disposablemail.org',
    'tempmail.com', 'tempmail.net', 'tempmail.org', 'tempmail.ru',
    'tmpmail.com', 'tmpmail.net', 'tmpmail.org', 'tmpmail.ru',
    'maildrop.cc', 'maildrop.net', 'maildrop.org', 'maildrop.com',
    'mailnull.com', 'mailnull.net', 'mailnull.org', 'mailnull.ru',
    'mailcatch.com', 'mailcatch.net', 'mailcatch.org', 'mailcatch.ru',
    'mailnesia.com', 'mailnesia.net', 'mailnesia.org', 'mailnesia.ru',
    'mailinator.net', 'mailinator.org', 'mailinator.com', 'mailinator.ru',
    'mailinator2.com', 'mailinator3.com', 'mailinator4.com', 'mailinator5.com',
    'mailinator6.com', 'mailinator7.com', 'mailinator8.com', 'mailinator9.com',
    'mailinator10.com', 'mailinator11.com', 'mailinator12.com', 'mailinator13.com',
    'mailinator14.com', 'mailinator15.com', 'mailinator16.com', 'mailinator17.com',
    'mailinator18.com', 'mailinator19.com', 'mailinator20.com', 'mailinator21.com',
    'mailinator22.com', 'mailinator23.com', 'mailinator24.com', 'mailinator25.com',
    'mailinator26.com', 'mailinator27.com', 'mailinator28.com', 'mailinator29.com',
    'mailinator30.com', 'mailinator31.com', 'mailinator32.com', 'mailinator33.com',
    'mailinator34.com', 'mailinator35.com', 'mailinator36.com', 'mailinator37.com',
    'mailinator38.com', 'mailinator39.com', 'mailinator40.com', 'mailinator41.com',
    'mailinator42.com', 'mailinator43.com', 'mailinator44.com', 'mailinator45.com',
    'mailinator46.com', 'mailinator47.com', 'mailinator48.com', 'mailinator49.com',
    'mailinator50.com', 'mailinator51.com', 'mailinator52.com', 'mailinator53.com',
    'mailinator54.com', 'mailinator55.com', 'mailinator56.com', 'mailinator57.com',
    'mailinator58.com', 'mailinator59.com', 'mailinator60.com', 'mailinator61.com',
    'mailinator62.com', 'mailinator63.com', 'mailinator64.com', 'mailinator65.com',
    'mailinator66.com', 'mailinator67.com', 'mailinator68.com', 'mailinator69.com',
    'mailinator70.com', 'mailinator71.com', 'mailinator72.com', 'mailinator73.com',
    'mailinator74.com', 'mailinator75.com', 'mailinator76.com', 'mailinator77.com',
    'mailinator78.com', 'mailinator79.com', 'mailinator80.com', 'mailinator81.com',
    'mailinator82.com', 'mailinator83.com', 'mailinator84.com', 'mailinator85.com',
    'mailinator86.com', 'mailinator87.com', 'mailinator88.com', 'mailinator89.com',
    'mailinator90.com', 'mailinator91.com', 'mailinator92.com', 'mailinator93.com',
    'mailinator94.com', 'mailinator95.com', 'mailinator96.com', 'mailinator97.com',
    'mailinator98.com', 'mailinator99.com', 'mailinator100.com'
  ];

  // Email validation function
  const validateEmail = (email: string): { isValid: boolean; message: string } => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(email)) {
      return { isValid: false, message: "Please enter a valid email address." };
    }

    const domain = email.split('@')[1]?.toLowerCase();
    
    if (!domain) {
      return { isValid: false, message: "Please enter a valid email address." };
    }

    // Check if domain is blocked
    if (blockedProviders.includes(domain)) {
      return { isValid: false, message: "Temporary email addresses are not allowed. Please use a valid email provider." };
    }

    // Check if domain is in trusted list (optional - you can remove this check if you want to allow any non-blocked domain)
    // if (!trustedProviders.includes(domain)) {
    //   return { isValid: false, message: "Please use a recognized email provider." };
    // }

    return { isValid: true, message: "" };
  };

  // Check if signup is disabled
  useEffect(() => {
    if (settings.disableSignup) {
      setError("Signup is currently disabled by the administrator.");
    } else {
      setError(null);
    }
  }, [settings.disableSignup]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing email
    if (name === 'email' && error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    // Validate email
    const emailValidation = validateEmail(form.email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message);
      return;
    }
    
    if (!form.terms) {
      setError("You must agree to the Terms of Service & Privacy Notice.");
      return;
    }
    setLoading(true);
    try {
      const auth = getAuth();
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      const user = userCredential.user;
      // Update display name
      await updateProfile(user, { displayName: form.name });
      await user.reload();
      // Store user info in Firestore
      if (!db) {
        setError("Database is not initialized. Please try again later.");
        setLoading(false);
        return;
      }
      await setDoc(doc(db, "users", user.uid), {
        name: form.name,
        email: form.email,
        createdAt: new Date().toISOString(),
      });
      setSuccess("Signup successful! You can now log in.");
      setForm({ name: "", email: "", password: "", terms: false });
      setTimeout(() => navigate("/signin"), 1500);
    } catch (err: any) {
      let message = err.message || "Signup failed";
      if (err.code === "auth/email-already-in-use") {
        message = "Email already in use";
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <Button 
              className="w-[166px] h-[49px] bg-[#daed6e] rounded-[20px] border border-solid border-[#e7e7e7] hover:bg-[#c5d85c]"
              onClick={() => navigate('/signin')}
            >
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">Log in</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-screen">
        <div className="w-full max-w-[480px] bg-white rounded-[20px] p-8 shadow-lg mt-[140px]">
          <div className="flex flex-col items-center mb-8">
            <img className="w-16 h-16 mb-4" alt="Beetle" src="/beetle.png" />
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Join RAWPA Now!
            </h1>
          </div>
          
          {settings.disableSignup && (
            <Alert className="mb-4 bg-yellow-50 border-yellow-200">
              <AlertTitle className="text-yellow-800">Signup Disabled</AlertTitle>
              <AlertDescription className="text-yellow-700">
                Signup is currently disabled by the administrator. Please try again later or contact support for assistance.
              </AlertDescription>
            </Alert>
          )}
          
          <form className="space-y-4" onSubmit={handleSubmit}>
            {error && !settings.disableSignup && (
              <div className="p-3 text-sm text-red-500 bg-red-50 rounded-lg">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 text-sm text-green-600 bg-green-50 rounded-lg">
                {success}
              </div>
            )}
            <Input
              name="name"
              className="h-12 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-base focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder="Name"
              value={form.name}
              onChange={handleChange}
              disabled={settings.disableSignup || loading}
              required
            />
            <Input
              name="email"
              type="email"
              className="h-12 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-base focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              disabled={settings.disableSignup || loading}
              required
            />
            <Input
              name="password"
              type="password"
              className="h-12 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-base focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder="Password (up to 64 characters)"
              value={form.password}
              onChange={handleChange}
              disabled={settings.disableSignup || loading}
              maxLength={64}
              required
            />

            <div className="flex items-start space-x-2">
              <Checkbox 
                name="terms"
                checked={form.terms}
                onChange={handleChange}
                disabled={settings.disableSignup || loading}
                required
              />
              <div className="text-sm text-black">
                <span>I agree to RAWPA's </span>
                <button 
                  type="button"
                  className="text-[#daed6e] hover:underline font-medium"
                  onClick={() => window.open('/terms', '_blank')}
                >
                  Terms of Service
                </button>
                <span> & </span>
                <button 
                  type="button"
                  className="text-[#daed6e] hover:underline font-medium"
                  onClick={() => window.open('/privacy', '_blank')}
                >
                  Privacy Policy
                </button>
              </div>
            </div>

            <Button 
              className="w-full h-12 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={settings.disableSignup || loading}
              type="submit"
            >
              <span className="font-['Space_Grotesk'] font-medium text-black text-base">{loading ? 'Signing Up...' : 'Sign Up'}</span>
            </Button>

            <div className="text-center mt-4">
              <span className="text-sm text-black opacity-80">Already have an account? </span>
              <button 
                type="button" 
                className="text-sm text-black font-medium hover:underline"
                onClick={() => navigate('/signin')}
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};